import { GridColDef } from '@mui/x-data-grid';

import { reverse } from 'named-urls';

import React from 'react';

import { StyledCreated } from 'containers/Alarms/components/AlarmList/styles';

import { formatDuration } from 'utils/formatters/time';
import { formatMoment } from 'utils/formatters/timezone';

import AlarmStatusChip from 'components/Chips/AlarmStatusChip';
import { defaultFormatters, defaultOperators, defaultRenderers } from 'components/Grid';

import { Button } from 'components/Button';
import { HomeSectionHeader, HomeSectionHeaderContainer, ViewAllButtonLink } from 'containers/Home/styles';
import { DATETIME_COLUMN_MIN_WIDTH, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR, RedAlertStatus } from 'core/constants';
import routes from 'core/routes';
import NoRowsOverlay from 'components/Grid/components/NoRowsOverlay';

import { JSONSchemaValue } from 'utils/api/modules/incidents/incidents.types';
import { AlarmResponse } from 'utils/api/modules/alarms/alarms.types';
import { HomeResponse } from 'utils/api/modules/common/common.types';
import { useStore } from 'utils/hooks/store';

import { CardButton, CardButtonsContainer, CardButtonText, DataGridContainer, StyledDataGrid } from './styles';
import { AlarmsIcon, TimedAlertsIcon, DevicesIcon, AlarmResponseProfilesIcon } from './Icons';

export const columns = [
  {
    field: 'subject',
    headerName: 'Alarm name',
    flex: 1,
  },
  {
    field: 'duration',
    headerName: 'Duration',
    valueGetter: (value: unknown) => formatDuration(value as number),
    maxWidth: 80,
  },
  {
    field: 'user',
    headerName: 'Started by',
    renderCell: defaultRenderers.user,
    valueFormatter: defaultFormatters.user,
    valueGetter: (value: unknown) => ({ value }),
    flex: 1,
  },
  {
    field: 'responder',
    headerName: 'Responder',
    renderCell: defaultRenderers.user,
    valueFormatter: defaultFormatters.user,
    valueGetter: (value: unknown) => ({ value }),
    flex: 1,
  },
  {
    field: 'alertStatus',
    headerName: 'Status',
    renderCell: ({ value }: { value: RedAlertStatus }) => <AlarmStatusChip status={value} size="medium" />,
  },
  {
    field: 'activated',
    filterOperators: defaultOperators.date_time(),
    headerName: 'Started',
    minWidth: DATETIME_COLUMN_MIN_WIDTH,
    valueGetter: (value: unknown) => ({ value }),
    renderCell: ({ value }: { value: JSONSchemaValue<string> }) => (
      <StyledCreated data-testid="home-alarm-started">
        {formatMoment(value?.value, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR)}
      </StyledCreated>
    ),
    valueFormatter: (value: JSONSchemaValue<string> | undefined) =>
      formatMoment(value?.value, DEFAULT_DATE_FORMAT_WITH_YEAR_AND_HOUR),
    flex: 1,
  },
] as unknown as GridColDef[];

const ManagerAlarmsPane: React.FunctionComponent = () => {
  const { home, routing } = useStore();

  const content = home.content as HomeResponse;

  return (
    <div aria-label="manager-role-alarms-pane">
      <HomeSectionHeaderContainer>
        <HomeSectionHeader>Lone Working</HomeSectionHeader>
      </HomeSectionHeaderContainer>
      <CardButtonsContainer>
        <CardButton
          onClick={() => {
            routing.push(reverse(routes.dashboard.alarms.red.toString()));
          }}
        >
          <AlarmsIcon />
          <CardButtonText>Alarms</CardButtonText>
        </CardButton>
        <CardButton
          onClick={() => {
            routing.push(reverse(routes.dashboard.alarms.amber.toString()));
          }}
        >
          <TimedAlertsIcon />
          <CardButtonText>Timed alerts</CardButtonText>
        </CardButton>
        <CardButton
          onClick={() => {
            routing.push(reverse(routes.dashboard.alarms.devices.toString()));
          }}
        >
          <DevicesIcon />
          <CardButtonText>Devices</CardButtonText>
        </CardButton>
        <CardButton
          onClick={() => {
            routing.push(reverse(routes.dashboard.alarms.escalationProfiles.main.toString()));
          }}
        >
          <AlarmResponseProfilesIcon />
          <CardButtonText>Alarm response profiles</CardButtonText>
        </CardButton>
      </CardButtonsContainer>
      <>
        <HomeSectionHeaderContainer>
          <HomeSectionHeader>Recent Alarms</HomeSectionHeader>
          {content.recentAlarms !== undefined && content.recentAlarms.length > 0 ? (
            <ViewAllButtonLink href={reverse(routes.dashboard.alarms.red.toString())}>
              <Button variant="outlined">View all</Button>
            </ViewAllButtonLink>
          ) : null}
        </HomeSectionHeaderContainer>
        {content.recentAlarms !== undefined ? (
          <DataGridContainer>
            <StyledDataGrid
              columns={columns}
              rows={content.recentAlarms || []}
              getRowId={(row: AlarmResponse) => row.uuid}
              hideFooter
              disableColumnMenu
              onRowClick={({ row }: { row: AlarmResponse }) => {
                routing.push(reverse(routes.dashboard.alarms.details.toString(), { alarmId: row.uuid }));
              }}
              slots={{
                noRowsOverlay: () => <NoRowsOverlay entity="alarm" />,
              }}
            />
          </DataGridContainer>
        ) : null}
      </>
    </div>
  );
};

export default ManagerAlarmsPane;
